import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Link,
} from '@mui/material';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNotifications } from '../../hooks/useNotifications';
import { resendActivationEmail } from '../../api/user';
import { resendActivationEmailStyles } from './ResendActivationEmail.styles';
import { t } from '../../utils/translations';

const ResendActivationEmailForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showError, showSuccess } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      showError(t('resendActivation.fillEmail'), t('common.error'));
      return;
    }

    if (!executeRecaptcha) {
      showError(t('auth.recaptchaFailed'), t('common.error'));
      return;
    }

    setIsLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('resend_activation');

      await resendActivationEmail({ email, recaptchaToken });
      showSuccess(t('resendActivation.emailSent'), t('common.success'));
      setEmail('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('resendActivation.unexpectedError');
      showError(errorMessage, t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={resendActivationEmailStyles.container}>
      <Paper elevation={3} sx={resendActivationEmailStyles.paper}>
        <Box sx={resendActivationEmailStyles.content}>
          <Typography variant="h4" component="h1" color="primary" sx={resendActivationEmailStyles.title}>
            Listum
          </Typography>

          <Typography variant="h6" component="h2" sx={{ mb: 2, textAlign: 'center' }}>
            {t('resendActivation.title')}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
            {t('resendActivation.description')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={resendActivationEmailStyles.form}>
            <TextField
              fullWidth
              label={t('resendActivation.emailLabel')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={resendActivationEmailStyles.input}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={resendActivationEmailStyles.submitButton}
            >
              {isLoading ? <CircularProgress size={24} /> : t('resendActivation.submitButton')}
            </Button>

            <Box sx={resendActivationEmailStyles.backLink}>
              <Typography variant="body2">
                <Link href="/login" underline="hover">
                  {t('resendActivation.backToLogin')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

const ResendActivationEmail: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  if (!recaptchaSiteKey) {
    return (
      <Container>
        <Typography color="error">
          {t('auth.recaptchaNotConfigured')}
        </Typography>
      </Container>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ResendActivationEmailForm />
    </GoogleReCaptchaProvider>
  );
};

export default ResendActivationEmail;
